import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Clapperboard, Search } from "lucide-react";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AdSlot } from "@/components/AdSlot";
import { MovieCard, gross } from "@/components/Movies";
import { MovieFaceOff } from "@/components/MovieFaceOff";

const PAGE = 30;
const chip = (on) => `shrink-0 px-3 h-8 rounded-full text-[12px] font-semibold border ${on ? "bg-ink text-white border-ink" : "bg-white text-ink border-[#e5e7eb] hover:border-gold"}`;
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};
const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", href);
};

export default function Movies() {
  const { decade: decadeParam } = useParams();
  const navigate = useNavigate();
  const decade = /^\d{4}s$/.test(decadeParam || "") ? decadeParam : "";
  const [summary, setSummary] = useState(null);
  const [genre, setGenre] = useState("");
  const [sort, setSort] = useState("rank");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [data, setData] = useState({ total: 0, items: [] });
  const [skip, setSkip] = useState(0);
  const decadeStat = summary?.decades.find((d) => d.decade === decade);

  useEffect(() => { api.get("/movies/summary").then((r) => setSummary(r.data)).catch(() => setSummary({ total: 0, decades: [], genres: [] })); }, []);
  useEffect(() => { setSkip(0); }, [decade]);
  useEffect(() => {
    api.get("/movies", { params: { decade: decade || undefined, genre: genre || undefined, q: query || undefined, sort, limit: PAGE, skip } }).then((r) => setData(r.data)).catch(() => setData({ total: 0, items: [] }));
  }, [decade, genre, query, sort, skip]);
  useEffect(() => {
    const n = decade ? decadeStat?.count : summary?.total;
    document.title = decade ? `The biggest films of the ${decade} — ${n ? n + " " : ""}box-office hits | OddlyTrue` : `The Movies — ${n ? n + " " : ""}biggest box-office films of all time | OddlyTrue`;
    setMeta("description", decade ? `Every ${decade} film in the all-time box-office list: premiere dates, directors, stars and worldwide takings${decadeStat ? `, ${gross(decadeStat.gross)} between them` : ""}.` : "Premiere dates, directors, stars and worldwide grosses for cinema's biggest films — plus the daily Box Office Face-Off quiz.");
    setCanonical(`${window.location.origin}/movies${decade ? `/${decade}` : ""}`);
    setMeta("robots", decade && summary && !decadeStat ? "noindex, follow" : "index, follow");
    const prev = document.querySelectorAll("script[data-movies-ld]");
    prev.forEach((el) => el.remove());
    if (data.items.length && skip === 0 && !query && !genre) {
      const ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.dataset.moviesLd = "1";
      ld.textContent = JSON.stringify({ "@context": "https://schema.org", "@type": "ItemList", name: document.title, itemListElement: data.items.map((m, i) => ({ "@type": "ListItem", position: i + 1, item: { "@type": "Movie", name: m.title, datePublished: m.release_date, director: (m.directors || []).map((d) => ({ "@type": "Person", name: d })), image: m.poster || undefined, sameAs: m.wiki_url || undefined } })) });
      document.head.appendChild(ld);
    }
    return () => document.querySelectorAll("script[data-movies-ld]").forEach((el) => el.remove());
  }, [decade, decadeStat, summary, data, skip, query, genre]);

  const pages = Math.max(1, Math.ceil(data.total / PAGE));
  const page = Math.floor(skip / PAGE) + 1;
  const reset = (fn) => (v) => { fn(v); setSkip(0); };

  return (
    <div className="min-h-screen bg-paper-light">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="movies-page">
        <div className="flex items-center gap-2">
          <Clapperboard className="w-4 h-4 text-gold" />
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold">{decade ? <><Link to="/movies" className="hover:underline">The Movies</Link> / {decade}</> : "The Movies"}</p>
        </div>
        <h1 data-testid="movies-h1" className="mt-2 text-4xl sm:text-5xl font-bold text-ink leading-tight">{decade ? `The biggest films of the ${decade}` : `The ${summary ? summary.total : ""} biggest films ever made`}</h1>
        <p className="mt-3 text-base text-ink-muted max-w-2xl">
          {decade ? <>{decadeStat ? `${decadeStat.count} ${decade} releases made the all-time box-office list` : `${decade} releases in the all-time box-office list`} — premiere dates, directors, stars and worldwide takings. {decadeStat && <span className="text-ink">{gross(decadeStat.gross)} banked between them.</span>}</>
            : <>Premiere dates, directors, stars and worldwide takings for cinema's box-office giants{summary?.from ? `, from ${summary.from} to ${summary.to}` : ""}. {summary?.gross > 0 && <span className="text-ink">{gross(summary.gross)} banked between them.</span>}</>}
        </p>

        {!decade && <MovieFaceOff />}

        <form onSubmit={(e) => { e.preventDefault(); setSkip(0); setQuery(q.trim()); }} className="mt-8 flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
            <input data-testid="movies-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, director, star or studio" className="w-full h-11 pl-9 pr-3 rounded-full border border-[#e5e7eb] bg-white text-sm text-ink focus:border-gold outline-none" />
          </div>
          <button type="submit" data-testid="movies-search-submit" className="px-5 h-11 rounded-full bg-ink text-white text-sm font-semibold hover:bg-gold hover:text-ink transition-colors">Search</button>
        </form>

        {summary && (
          <>
            <nav aria-label="Decades" className="mt-4 flex gap-2 overflow-x-auto pb-2 md:flex-wrap md:overflow-visible" data-testid="movies-decades">
              <Link to="/movies" data-testid="movies-decade-all" className={chip(!decade)}>All decades</Link>
              {summary.decades.map((d) => <Link key={d.decade} to={`/movies/${d.decade}`} data-testid={`movies-decade-${d.decade}`} className={chip(decade === d.decade)}>{d.decade} <span className="opacity-60">{d.count}</span></Link>)}
            </nav>
            <div className="mt-1 flex gap-2 overflow-x-auto pb-2 md:flex-wrap md:overflow-visible" data-testid="movies-genres">
              <button type="button" data-testid="movies-genre-all" onClick={() => reset(setGenre)("")} className={chip(!genre)}>All genres</button>
              {summary.genres.map((g) => <button key={g.genre} type="button" data-testid={`movies-genre-${slug(g.genre)}`} onClick={() => reset(setGenre)(g.genre)} className={chip(genre === g.genre)}>{g.genre} {!decade && <span className="opacity-60">{g.count}</span>}</button>)}
            </div>
          </>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
          <p data-testid="movies-total" className="text-[12px] text-ink-muted">{data.total} film{data.total === 1 ? "" : "s"}{decade ? ` · ${decade}` : ""}{genre ? ` · ${genre}` : ""}{query ? ` · "${query}"` : ""}</p>
          <div className="flex gap-1 text-[12px]" data-testid="movies-sort">
            <button type="button" data-testid="movies-sort-rank" onClick={() => reset(setSort)("rank")} className={chip(sort === "rank")}>Biggest gross</button>
            <button type="button" data-testid="movies-sort-year" onClick={() => reset(setSort)("year")} className={chip(sort === "year")}>Newest first</button>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="movies-list">
          {data.items.map((m, i) => <MovieCard key={m.id} m={m} testid={`movies-item-${i + 1}`} />)}
        </div>
        {data.total === 0 && <p data-testid="movies-empty" className="mt-6 text-sm text-ink-muted">No films match.</p>}

        {pages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button type="button" data-testid="movies-prev" disabled={page <= 1} onClick={() => setSkip(skip - PAGE)} className="px-4 h-10 rounded-full border border-[#e5e7eb] bg-white text-sm font-semibold text-ink disabled:opacity-40">← Prev</button>
            <span className="text-[13px] text-ink-muted">Page {page} of {pages}</span>
            <button type="button" data-testid="movies-next" disabled={page >= pages} onClick={() => setSkip(skip + PAGE)} className="px-4 h-10 rounded-full border border-[#e5e7eb] bg-white text-sm font-semibold text-ink disabled:opacity-40">Next →</button>
          </div>
        )}
        {decade && (
          <nav aria-label="Other decades" data-testid="movies-decade-footer" className="mt-10 border-t border-ink/10 pt-6">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold">More decades</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {(summary?.decades || []).filter((d) => d.decade !== decade).map((d) => <Link key={d.decade} to={`/movies/${d.decade}`} className="text-sm font-semibold text-ink hover:text-gold">The biggest films of the {d.decade} →</Link>)}
              <button type="button" onClick={() => navigate("/movies#faceoff")} className="text-sm font-semibold text-ink hover:text-gold">Play the Box Office Face-Off →</button>
            </div>
          </nav>
        )}
        <AdSlot id="adsense-movies" slot="MID" size="rect300" className="mt-8" />
      </main>
      <Footer />
    </div>
  );
}
